'use client';

import { useState } from 'react';
import useSWR from 'swr';
import { useSWRConfig } from 'swr';
import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Link2, Unlink, Plus } from 'lucide-react';
import type { UserBusinessCommunityLink, UserBusinessLinkableCommunity } from '@/types';
import { revalidateAfterCommunityBusinessListingChange } from '@/lib/revalidate-cache';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

const statusLabel: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Listed',
  REJECTED: 'Rejected',
};

function getApiErrorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const d = e.response?.data as unknown;
    if (d && typeof d === 'object' && d !== null && 'error' in d) {
      const msg = (d as { error: unknown }).error;
      if (msg != null && String(msg).trim() !== '') return String(msg);
    }
    if (typeof d === 'string' && d.trim() !== '') return d;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

type Response = {
  links: UserBusinessCommunityLink[];
  linkableCommunities: UserBusinessLinkableCommunity[];
};

export function BusinessProfileLinkedCommunities({ businessId }: { businessId: string }) {
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [pickCommunityId, setPickCommunityId] = useState('');
  const { mutate: globalMutate } = useSWRConfig();
  const { data, error, isLoading, mutate } = useSWR<Response>(
    businessId ? `/api/user/business/${businessId}/community-links` : null,
    fetcher
  );

  const links = data?.links ?? [];
  const linkableCommunities = data?.linkableCommunities ?? [];

  const handleRemove = async (communityId: string) => {
    setRemovingId(communityId);
    try {
      await axiosInstance.delete(`/api/communities/${communityId}/business-links`, {
        data: { businessIds: [businessId] },
      });
      toast.success('Listing removed');
      await Promise.all([
        mutate(),
        revalidateAfterCommunityBusinessListingChange(globalMutate, communityId),
      ]);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not remove listing'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleAdd = async (communityId: string, requiresApproval: boolean) => {
    setAddingId(communityId);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/business-links`, {
        businessIds: [businessId],
      });
      toast.success(
        requiresApproval ? 'Listing request sent for admin approval' : 'Added to directory'
      );
      setPickCommunityId('');
      await Promise.all([
        mutate(),
        revalidateAfterCommunityBusinessListingChange(globalMutate, communityId),
      ]);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not add listing'));
    } finally {
      setAddingId(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="profile-card">
        <CardContent className="flex items-center gap-3 py-10 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin shrink-0" />
          <span className="text-sm font-medium">Loading directory listings…</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-destructive/30 rounded-3xl overflow-hidden">
        <CardContent className="py-8 text-sm text-destructive">
          Could not load linked communities. Try again later.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="profile-card">
      <CardHeader className="px-5 md:px-7 pt-6 pb-2 md:pt-7 space-y-1">
        <CardTitle className="text-lg md:text-xl font-semibold font-display tracking-tight text-foreground flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
            <Link2 className="h-5 w-5" aria-hidden />
          </span>
          Directory listings
        </CardTitle>
        <CardDescription className="pl-[52px] text-sm leading-relaxed max-w-2xl">
          Communities where this business appears in the member directory through your listings.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-5 md:px-7 pb-6 md:pb-8 pt-2 space-y-5">
        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {linkableCommunities.length > 0 ? (
              <>
                You have not linked this business to a curated directory yet. Choose a community
                below to add a listing, or open a community’s{' '}
                <span className="font-medium text-foreground/90">Business</span> area from the app.
              </>
            ) : (
              <>
                You have not linked this business to any community directory yet. When curated
                listings are enabled in a community you belong to, you can add it here or from that
                community’s <span className="font-medium text-foreground/90">Business</span> tab.
              </>
            )}
          </p>
        ) : (
          <ul className="space-y-3">
            {links.map((link) => {
              const isRejected = link.status === 'REJECTED';
              return (
                <li
                  key={link.id}
                  className="profile-inset flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {link.communityLogo ? (
                      <img
                        src={link.communityLogo}
                        alt=""
                        className="h-11 w-11 rounded-xl object-cover ring-1 ring-border/40 shrink-0"
                      />
                    ) : (
                      <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0 ring-1 ring-primary/15">
                        {link.communityName.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground truncate">{link.communityName}</p>
                      <Badge
                        variant={
                          link.status === 'APPROVED'
                            ? 'default'
                            : isRejected
                              ? 'destructive'
                              : 'secondary'
                        }
                        className="mt-1 font-medium"
                      >
                        {statusLabel[link.status] ?? link.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0 sm:pl-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl gap-1.5"
                      disabled={removingId === link.communityId}
                      onClick={() => void handleRemove(link.communityId)}
                    >
                      {removingId === link.communityId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Unlink className="h-4 w-4" />
                      )}
                      Remove
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {linkableCommunities.length > 0 && (
          <div className="rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 md:p-5 space-y-3">
            <p className="text-sm font-medium text-foreground">Add to another community</p>
            {linkableCommunities.length === 1 ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {linkableCommunities[0].communityLogo ? (
                    <img
                      src={linkableCommunities[0].communityLogo}
                      alt=""
                      className="h-10 w-10 rounded-lg object-cover ring-1 ring-border/40 shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold shrink-0">
                      {linkableCommunities[0].communityName.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium truncate">
                    {linkableCommunities[0].communityName}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="rounded-xl gap-2 shrink-0"
                  disabled={addingId === linkableCommunities[0].communityId}
                  onClick={() =>
                    void handleAdd(
                      linkableCommunities[0].communityId,
                      linkableCommunities[0].businessLinkApprovalRequired
                    )
                  }
                >
                  {addingId === linkableCommunities[0].communityId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  List here
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:flex-wrap">
                <Select
                  value={pickCommunityId}
                  onValueChange={setPickCommunityId}
                  disabled={!!addingId}
                >
                  <SelectTrigger className="w-full sm:max-w-md rounded-xl bg-background/80 dark:bg-card/80 border-border">
                    <SelectValue placeholder="Choose a community…" />
                  </SelectTrigger>
                  <SelectContent>
                    {linkableCommunities.map((c) => (
                      <SelectItem key={c.communityId} value={c.communityId}>
                        {c.communityName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  className="rounded-xl gap-2 shrink-0"
                  disabled={
                    !pickCommunityId ||
                    !!addingId ||
                    !linkableCommunities.some((c) => c.communityId === pickCommunityId)
                  }
                  onClick={() => {
                    const opt = linkableCommunities.find((c) => c.communityId === pickCommunityId);
                    if (opt) void handleAdd(opt.communityId, opt.businessLinkApprovalRequired);
                  }}
                >
                  {addingId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add listing
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground leading-relaxed">
              Only communities you belong to with curated directory listings are listed. Approval
              may be required per community rules.
            </p>
          </div>
        )}

        {links.length > 0 && linkableCommunities.length === 0 && (
          <p className="text-xs text-muted-foreground leading-relaxed">
            There are no additional communities available to add this listing to right now based on
            your memberships and each community’s directory settings.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
